/**
 * BulkRelayRebootModal — Sprint 4.1.2.
 *
 * Modal "Reiniciar Relé" para uso em multi-seleção. Mesmo layout do
 * modal usado no Device Details (single device), agora aplicando a N.
 *
 * Campo Duração (segundos): mín 1, máx 300, default 10.
 *
 * Aplica via POST /api/admin/fleet-map/bulk-command com type=relay_reboot
 * (endpoint 4.1.1 já suporta payload {duration_s}).
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { bulkCommand } from "../../lib/api.js";
import { confirmMode } from "../ConfirmCriticalAction.jsx";
import BulkProtectionBreakdown from "../BulkProtectionBreakdown.jsx";

const MIN_S = 1;
const MAX_S = 300;
const DEFAULT_S = 10;

export default function BulkRelayRebootModal({
  open,
  deviceIds,         // string[]
  breakdown,         // MAP-UX-1E — { total, eligible, protectedCount, protectedIds, offlineCount }
  onClose,
  onDone,            // (summary) => void  — chama refresh
}) {
  const [duration, setDuration] = useState(DEFAULT_S);
  const [secret, setSecret] = useState(""); // S3.10A — senha/ADMIN_TOKEN
  const mode = confirmMode();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (open) {
      setDuration(DEFAULT_S);
      setSecret("");
      setBusy(false);
      setError(null);
      setSummary(null);
    }
  }, [open, deviceIds]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;
  const n = deviceIds.length;
  // MAP-UX-1E — recorte de proteção (default seguro quando a barra não passa).
  const bd = breakdown || {
    total: n, eligible: n, protectedCount: 0, protectedIds: [], offlineCount: 0,
  };
  const allProtected = bd.protectedCount > 0 && bd.eligible === 0;

  const handleConfirm = async () => {
    // MAP-UX-1E — se TODOS os selecionados são protegidos (produção), nenhum
    // receberia o reinício: bloqueia o submit p/ evitar falsa sensação de ação.
    if (allProtected) {
      setError(
        `Todos os ${bd.total} dispositivo(s) selecionados são protegidos `
        + "(produção) — nenhum receberá o reinício.",
      );
      return;
    }
    const dur = Number(duration);
    if (!Number.isInteger(dur) || dur < MIN_S || dur > MAX_S) {
      setError(`Duração deve ser inteiro entre ${MIN_S} e ${MAX_S} segundos.`);
      return;
    }
    if (!secret) {
      setError(`Confirme com ${mode.label.toLowerCase()} para reiniciar o relé.`);
      return;
    }
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const r = await bulkCommand(deviceIds, "relay_reboot", {
        payload: { duration_s: dur },
        confirm: secret,
      });
      setSecret("");
      setSummary(r);
      if (onDone) onDone(r);
    } catch (e) {
      setError(e?.message || "Erro em bulk relay_reboot");
    } finally {
      setBusy(false);
    }
  };

  // S3.10A fix — portaliza para document.body: a BulkActionBar usa
  // -translate-x-1/2 (transform), que cria containing block para position:fixed
  // e prendia este modal na caixa da barra. No body, o overlay fixed centraliza
  // na viewport (igual ao ConfirmCriticalAction).
  return createPortal(
    <div
      className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-noc-panel border border-noc-err/30 rounded shadow-2xl w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-noc-err">⟲ Reiniciar Relé (bulk)</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-noc-muted hover:text-noc-text text-lg leading-none disabled:opacity-50"
          >×</button>
        </div>

        <div className="text-xs space-y-2">
          {/* MAP-UX-1E — breakdown elegíveis/protegidos/offline dentro do modal */}
          <BulkProtectionBreakdown
            total={bd.total}
            eligible={bd.eligible}
            protectedCount={bd.protectedCount}
            protectedIds={bd.protectedIds}
            offlineCount={bd.offlineCount}
            actionLabel="o reinício do relé"
          />
          {n <= 5 && (
            <div className="text-[10px] text-noc-muted/80 font-mono break-all">
              {deviceIds.join(", ")}
            </div>
          )}
        </div>

        <div className="text-xs text-noc-text">
          Esta ação vai <span className="text-noc-err font-bold">DESLIGAR</span> a
          energia do equipamento conectado por aproximadamente{" "}
          <span className="font-mono text-noc-warn">{duration}s</span> e religar em seguida.
        </div>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted uppercase">Duração (segundos)</span>
          <input
            type="number"
            min={MIN_S}
            max={MAX_S}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            disabled={busy}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono"
          />
          <span className="text-[10px] text-noc-muted">
            Mínimo {MIN_S}, máximo {MAX_S}. Padrão {DEFAULT_S}.
          </span>
        </label>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted uppercase">{mode.label} (ação crítica)</span>
          <input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            disabled={busy}
            placeholder={mode.placeholder}
            className="w-full bg-noc-bg border border-noc-err/30 rounded px-2 py-1 font-mono"
          />
        </label>

        <div className="text-[10px] text-noc-muted bg-noc-bg/50 rounded p-2 border border-noc-border/40">
          Latência esperada: cada comando vai virar <span className="font-mono text-noc-accent">delivered</span> no
          próximo heartbeat (até ~60s) e <span className="font-mono text-noc-ok">acked</span> após
          religar + mais um heartbeat. Total aproximado por device: 2 a 3 minutos.
          Devices com relé já OFF podem ser rejeitados pelo firmware
          com <span className="font-mono">relay_already_off</span>.
        </div>

        {summary && (
          <div className="text-xs bg-noc-bg/50 rounded p-2 border border-noc-border/40">
            <div>Total: <span className="font-mono">{summary.total}</span></div>
            <div className="text-noc-ok">Enfileirados: <span className="font-mono">{summary.enqueued}</span></div>
            {summary.failed > 0 && (
              <div className="text-noc-err">Falhas: <span className="font-mono">{summary.failed}</span></div>
            )}
          </div>
        )}

        {error && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-xs px-3 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text disabled:opacity-50"
          >Cancelar</button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || summary !== null || !secret || allProtected}
            title={allProtected ? "Todos os selecionados são protegidos (produção)" : undefined}
            className="text-xs px-3 py-1 rounded bg-noc-err/15 hover:bg-noc-err/25 border border-noc-err/40 text-noc-err font-semibold disabled:opacity-50"
          >{busy ? "Enviando…" : (summary ? "Concluído" : "Confirmar reinício")}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
